/**
 * Home page constants (specs/home-page). Copy, test ids, query timings and the routes Home links to.
 */

/** `/app` is Home, the lobby: cold start and every "go home" flow (e.g. abandoning Create) land here (AC-1, AC-4b). */
export const HOME_PATH = '/app' as const;

/** Home's queries are refetched on focus; keep them fresh for 5 min and cached for 15 (AC-15 lineage). */
export const HOME_QUERY_STALE_TIME_MS = 5 * 60 * 1000;
export const HOME_QUERY_GC_TIME_MS = 15 * 60 * 1000;
/** Quest progress moves as the player acts elsewhere; keep it fresh for 2 min and refetch on focus. */
export const HOME_QUESTS_STALE_TIME_MS = 2 * 60 * 1000;
/** Three tasks per week (mirrors the server's QUEST_TASKS_PER_WEEK). */
export const HOME_QUESTS_PER_WEEK = 3;
/** The pseudo task key the Weekly card reports when a guest taps the locked chest bar (AC-24). */
export const HOME_CHEST_LOCK_KEY = 'chest';
/** Last results shows the two newest finished 1v1s (AC-39). */
export const HOME_RESULTS_LIMIT = 2;
/** The daily claim ladder is seven days long (mirrors DAILY_CLAIM_LADDER). */
export const HOME_STREAK_DAYS = 7;

/** The design is authored at 402pt; on wide web the body is a centred column (§7). */
export const HOME_COLUMN_MAX_WIDTH = 480;

export const HOME_ROUTES = {
  ARENA: '/app/arena',
  ARENA_OPEN_BOARD: '/app/arena?view=open',
  CREATE: '/app/create',
  /** The Fixtures screen (hidden tab since AC-2); the slate card's "See all matches" opens it (AC-11b). */
  FIXTURES: '/app/gameResults',
  arenaChallenge: (challengeId: string) =>
    `/app/arena?expandChallenge=${encodeURIComponent(challengeId)}`,
} as const;

export const HOME_COPY = {
  SLATE_LABEL: "Today's challenge",
  SLATE_SUBTITLE: 'Our picks, ready to send a friend',
  SLATE_SEND: 'Send to a Friend',
  SLATE_SEND_A11Y: 'Open Create Challenge with this slate',
  SLATE_MONKEY_LINE_1: 'Beat the',
  SLATE_MONKEY_LINE_2: 'Monkey',
  SLATE_MONKEY_A11Y: 'Challenge the Monkey with this slate',
  SLATE_EMPTY: "Tomorrow's challenge lands at midnight",
  SLATE_SEE_ALL: 'See all matches',
  SLATE_SEE_ALL_A11Y: 'Open all fixtures',
  SLATE_NEW_IN: 'New in',
  SLATE_NEW_NOW: 'now',
  KICKOFF_STARTED: 'Live',
  STREAK_LABEL: 'Daily streak',
  STREAK_SUBTITLE: 'Claim coins every day',
  STREAK_DAY: 'day',
  STREAK_DAYS: 'days',
  STREAK_NEXT_IN: 'Next in',
  STREAK_COMPLETE: 'All 7 days collected',
  STREAK_OPEN_HINT: 'Opens daily rewards',
  streakCollect: (coins: number) => `Collect +${coins}`,
  streakDot: (day: number) => `${day}`,
  WEEKLY_LABEL: 'Weekly quests',
  WEEKLY_CHEST_FINISH: 'Finish all 3',
  WEEKLY_CHEST_OPENED: 'Chest opened',
  /** A guest's chest is never granted (nothing is banked for a guest); the bar offers sign-up instead (AC-24). */
  WEEKLY_CHEST_GUEST: 'Sign up to open the chest',
  WEEKLY_LOCKED_A11Y: 'Sign up to unlock this quest',
  CHEST_TITLE: 'Chest opened!',
  CHEST_SUBTITLE: 'All three weekly quests done.',
  CHEST_COLLECT: 'Collect',
  /** The action named in the guest sign-up prompt when a guest taps a locked quest. */
  GUEST_LOCK_ACTION: 'add friends',
  /** The action named in the guest sign-up prompt when a guest taps the locked chest. */
  GUEST_CHEST_ACTION: 'open the chest',
  coinsPlus: (coins: number) => `+${coins} coins`,
  ACTIVE_YOU: 'You',
  ACTIVE_LEADING: 'leading',
  ACTIVE_BEHIND: 'behind',
  ACTIVE_LEVEL: 'level',
  gamesLeft: (count: number) => `${count} ${count === 1 ? 'game' : 'games'} left`,
  INVITE_CHALLENGED_YOU: 'challenged you',
  INVITE_ACCEPT: 'Accept',
  ONE_V_ONE: '1v1',
  GROUP: 'Group',
  GROUP_FALLBACK_NAME: 'Group challenge',
  VS_PREFIX: 'vs',
  RESULTS_LABEL: 'Last results',
  RESULTS_SUBTITLE: 'Your latest finished challenges',
  RESULTS_CTA: 'Challenge a friend',
  RESULTS_EMPTY: 'No finished challenges yet',
  RESULTS_FIND_RIVAL: 'Find a rival',
  RESULT_WON: 'Won',
  RESULT_LOST: 'Lost',
  RESULT_DRAW: 'Draw',
  REGION_ERROR_TITLE: "Couldn't load",
  REGION_ERROR_RETRY: 'Retry',
  ACCEPTED: 'Challenge accepted',
  ACCEPT_FAILED: 'Failed to accept challenge',
  needCoins: (coins: number) => `You need ${coins} coins to accept this challenge.`,
} as const;

export const HOME_TEST_IDS = {
  screen: 'home-screen',
  scroll: 'home-scroll',
  slateRegion: 'home-slate-region',
  slateCard: 'home-slate-card',
  slateCountdown: 'home-slate-countdown',
  slateMatch: 'home-slate-match',
  slatePick: 'home-slate-pick',
  slateSend: 'home-slate-send',
  slateMonkey: 'home-slate-monkey',
  slateEmpty: 'home-slate-empty',
  slateSeeAll: 'home-slate-see-all',
  slateSkeleton: 'home-slate-skeleton',
  streakRegion: 'home-streak-region',
  streakCard: 'home-streak-card',
  streakDays: 'home-streak-days',
  streakDot: 'home-streak-dot',
  streakStatus: 'home-streak-status',
  streakModal: 'home-daily-reward-modal',
  weeklyRegion: 'home-weekly-region',
  weeklyCard: 'home-weekly-card',
  weeklyPitch: 'home-weekly-pitch',
  weeklyCountdown: 'home-weekly-countdown',
  weeklyNode: 'home-weekly-node',
  weeklyChestRow: 'home-weekly-chest',
  weeklySkeleton: 'home-weekly-skeleton',
  chestModal: 'home-chest-modal',
  chestModalDismiss: 'home-chest-modal-dismiss',
  activeTile: 'home-active-tile',
  activeStatus: 'home-active-status',
  activeScore: 'home-active-score',
  activeKickoff: 'home-active-kickoff',
  activeGamesLeft: 'home-active-games-left',
  inviteTile: 'home-invite-tile',
  inviteAccept: 'home-invite-accept',
  tileSkeleton: 'home-tile-skeleton',
  resultsCard: 'home-results-card',
  resultsRow: 'home-results-row',
  resultsEmpty: 'home-results-empty',
  resultsCta: 'home-results-cta',
  resultsFindRival: 'home-results-find-rival',
  resultsSkeleton: 'home-results-skeleton',
  regionError: 'home-region-error',
  regionErrorRetry: 'home-region-error-retry',
} as const;
